/*
 * <MediaPicker/>
 * ──────────────
 * Modal launched from an <EditableImage/> or <EditableVideo/> pencil.
 * Three ways to set a source:
 *   1. Choose from the Library (existing uploads + registered externals)
 *   2. Upload a new file (image ≤10 MB, video ≤50 MB)
 *   3. Paste an external URL (Unsplash, YouTube, Vimeo, MP4, …)
 *
 * `onPick(item)` is called with `{url, kind, id?}` when the user selects a
 * source.
 */
import React, { useEffect, useRef, useState } from "react";
import { X, Upload, Link as LinkIcon, ImageIcon, Video, Loader2, Trash2, Check, Search } from "lucide-react";
import { api, formatError, BACKEND_URL } from "@/lib/api";
import { chunkedMediaUpload } from "@/lib/chunkedUpload";
import { toast } from "sonner";

const TABS = [
  { id: "library",  label: "Library" },
  { id: "upload",   label: "Upload" },
  { id: "external", label: "External URL" },
];

/** Make the API-relative /api/cms/media/xxx.jpg path absolute so it works
 * both in preview and production without the frontend caring where the
 * backend actually lives. */
function absolutize(url) {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export default function MediaPicker({ kind = "image", onPick, onClose, currentUrl }) {
  const [tab, setTab] = useState("library");
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [externalAlt, setExternalAlt] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cms/media?kind=${kind}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setLibrary(data?.media || []);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    try {
      // Chunked upload — a 756 MB module video used to fail at ~40-50%
      // because the single multipart POST hit the ingress body cap or
      // proxy timeout mid-body. Chunking splits it into ~8 MB requests
      // and retries per-chunk on transient failures.
      const data = await chunkedMediaUpload(file, kind, (p) => setUploadPct(p));
      toast.success("Uploaded");
      onPick({ ...data, url: absolutize(data.url) });
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); setUploadPct(0); }
  };

  const addExternal = async () => {
    if (!externalUrl.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/cms/media", { url: externalUrl.trim(), kind, alt: externalAlt.trim() });
      toast.success("Added");
      onPick({ ...data, url: absolutize(data.url) });
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remove from library? Existing pages using this URL will break.")) return;
    try {
      await api.delete(`/admin/cms/media/${id}`);
      setLibrary((prev) => prev.filter((it) => it.id !== id));
      toast.success("Removed");
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="cms-drawer-backdrop" onClick={onClose}>
      <div className="cms-drawer cms-drawer-lg" onClick={(e) => e.stopPropagation()} data-testid="cms-media-picker">
        <div className="flex items-center justify-between px-5 py-4 border-b border-shell-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary flex items-center gap-1.5">
              {kind === "image" ? <ImageIcon className="w-3 h-3" /> : <Video className="w-3 h-3" />}
              Media Picker · {kind}
            </p>
            <h3 className="font-display text-xl mt-0.5">Choose a {kind}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-shell-border/30" data-testid="cms-media-picker-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-b border-shell-border">
          {TABS.map((t) => (
            <button
              key={t.id} type="button"
              onClick={() => setTab(t.id)}
              data-testid={`cms-media-tab-${t.id}`}
              className={[
                "rounded-full px-3 py-1.5 text-xs font-semibold transition",
                tab === t.id ? "bg-primary text-white" : "bg-shell-border/30 text-muted-foreground hover:bg-shell-border/50",
              ].join(" ")}
            >{t.label}</button>
          ))}
          <div className="ml-auto text-[11px] text-muted-foreground">
            {kind === "image" ? "PNG / JPG / WEBP / GIF · max 10 MB" : "MP4 / WEBM · max 50 MB (or paste YouTube/Vimeo URL)"}
          </div>
        </div>

        {/* LIBRARY TAB */}
        {tab === "library" && (
          <div className="p-5 flex-1 overflow-auto">
            <div className="relative mb-4">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
              <input
                type="text" value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="Search by filename, alt or tag…"
                className="w-full rounded-full border border-shell-border bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary/40"
                data-testid="cms-media-search"
              />
            </div>
            {loading ? (
              <div className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : library.length === 0 ? (
              <p className="text-sm text-muted-foreground italic" data-testid="cms-media-empty">
                Library is empty for {kind}. Upload one below or paste an external URL.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {library.map((it) => {
                  const full = absolutize(it.url);
                  const isCurrent = currentUrl && (currentUrl === full || currentUrl === it.url);
                  return (
                    <div key={it.id} className={`relative rounded-xl border-2 overflow-hidden bg-shell-border/10 ${isCurrent ? "border-primary" : "border-shell-border"}`} data-testid={`cms-media-item-${it.id}`}>
                      {kind === "image" ? (
                        <img src={full} alt={it.alt || it.filename} className="w-full h-32 object-cover" />
                      ) : (
                        <div className="w-full h-32 grid place-items-center bg-slate-900 text-white">
                          <Video className="w-8 h-8 opacity-70" />
                        </div>
                      )}
                      <div className="p-2 flex items-center gap-1">
                        <p className="text-[11px] truncate flex-1" title={it.filename}>{it.filename}</p>
                        <button type="button" onClick={() => removeItem(it.id)} className="text-rose-600 hover:bg-rose-500/10 rounded p-1" title="Remove" data-testid={`cms-media-remove-${it.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <button type="button" onClick={() => onPick({ ...it, url: full })} data-testid={`cms-media-pick-${it.id}`} className="absolute inset-0 bg-primary/0 hover:bg-primary/10 transition flex items-center justify-center opacity-0 hover:opacity-100">
                        <span className="rounded-full bg-primary text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1"><Check className="w-3 h-3" /> Use this</span>
                      </button>
                      {it.source === "external" && (
                        <span className="absolute top-1 left-1 text-[9px] bg-shell-side text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">Ext</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* UPLOAD TAB */}
        {tab === "upload" && (
          <div className="p-5 flex-1">
            <label className="block rounded-2xl border-2 border-dashed border-shell-border p-8 text-center cursor-pointer hover:border-primary/40 transition">
              <input ref={fileRef} type="file" className="hidden"
                     accept={kind === "image" ? "image/png,image/jpeg,image/webp,image/gif" : "video/mp4,video/webm,video/quicktime"}
                     onChange={(e) => doUpload(e.target.files?.[0])}
                     data-testid="cms-media-upload-input" />
              {uploading ? (
                <div className="max-w-xs mx-auto">
                  <div className="inline-flex items-center gap-2 text-primary">
                    <Loader2 className="w-5 h-5 animate-spin" /> Uploading {Math.round(uploadPct * 100)}%
                  </div>
                  {/* Progress bar for large-file chunked uploads. Fill
                      width mirrors the pct so a 756 MB video is
                      observably making forward progress even when a
                      single chunk retry stalls for a moment. */}
                  <div className="mt-2 h-1.5 w-full bg-primary/10 rounded-full overflow-hidden" data-testid="cms-media-upload-progress">
                    <div className="h-full bg-primary transition-all"
                         style={{ width: `${Math.round(uploadPct * 100)}%` }} />
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">Large files upload in ~8 MB chunks; don't close this tab.</p>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-primary" />
                  <p className="mt-2 font-semibold">Click to browse</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {kind === "image" ? "PNG / JPG / WEBP / GIF · max 10 MB" : "MP4 / WEBM / MOV · max 2 GB (chunked)"}
                  </p>
                </>
              )}
            </label>
          </div>
        )}

        {/* EXTERNAL URL TAB */}
        {tab === "external" && (
          <div className="p-5 flex-1 space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Public URL</label>
              <input
                type="url" value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder={kind === "image" ? "https://images.unsplash.com/…" : "https://youtube.com/watch?v=… or https://…/video.mp4"}
                className="w-full mt-1 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
                data-testid="cms-media-external-url"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Alt text (optional)</label>
              <input
                type="text" value={externalAlt}
                onChange={(e) => setExternalAlt(e.target.value)}
                placeholder="Describe the media for accessibility & SEO"
                className="w-full mt-1 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
                data-testid="cms-media-external-alt"
              />
            </div>
            <button
              type="button" onClick={addExternal} disabled={saving || !externalUrl.trim()}
              className="cms-btn cms-btn-primary"
              data-testid="cms-media-external-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LinkIcon className="w-3.5 h-3.5" />} Add to library & use
            </button>
            <p className="text-[11px] text-muted-foreground">
              The URL is added to your Library and immediately assigned to the block. YouTube share links auto-convert to embed URLs.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
